/*
  Milion Style — theme.js

  Nota de arquitetura: todo o HTML do carrinho vive em sections/cart-drawer.liquid.
  Este arquivo nunca monta markup de carrinho; ele pede a section re-renderizada
  para a Cart AJAX API e troca o nó. Por isso todos os listeners usam delegação
  no document — os elementos internos são substituídos a cada atualização.
*/

(function () {
  'use strict';

  var settings = window.themeSettings || {};
  var CART_SECTION = 'cart-drawer';

  /* ==================== UTIL ==================== */

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ==================== DRAWERS ==================== */

  var activeDrawer = null;
  var lastFocused = null;

  function openDrawer(drawer) {
    if (!drawer) return;

    lastFocused = document.activeElement;
    activeDrawer = drawer;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    var overlay = document.querySelector(drawer.dataset.overlay || '#CartDrawerOverlay');
    if (overlay) overlay.classList.add('is-open');

    document.querySelectorAll('[data-open-cart], [data-open-drawer]').forEach(function (trigger) {
      var controls = trigger.getAttribute('aria-controls');
      if (controls === drawer.id) trigger.setAttribute('aria-expanded', 'true');
    });

    document.body.classList.add('drawer-open');

    // O foco precisa esperar a transição começar, senão o navegador
    // rola a página até o elemento antes dele estar na tela.
    window.requestAnimationFrame(function () {
      var target = drawer.querySelector('[data-close-cart], [data-close-drawer]') || drawer;
      target.focus({ preventScroll: true });
    });
  }

  function closeDrawer(drawer) {
    drawer = drawer || activeDrawer;
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    var overlay = document.querySelector(drawer.dataset.overlay || '#CartDrawerOverlay');
    if (overlay) overlay.classList.remove('is-open');

    document.querySelectorAll('[data-open-cart], [data-open-drawer]').forEach(function (trigger) {
      var controls = trigger.getAttribute('aria-controls');
      if (controls === drawer.id) trigger.setAttribute('aria-expanded', 'false');
    });

    if (!document.querySelector('.drawer.is-open, .cart-drawer.is-open')) {
      document.body.classList.remove('drawer-open');
    }

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }

    activeDrawer = null;
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !activeDrawer) return;

    var focusables = Array.prototype.filter.call(
      activeDrawer.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (focusables.length === 0) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openCart() {
    openDrawer(document.getElementById('CartDrawer'));
  }

  /* ==================== CART ==================== */

  function applyCartSections(sections) {
    if (!sections || !sections[CART_SECTION]) return;

    var parsed = new DOMParser().parseFromString(sections[CART_SECTION], 'text/html');
    var fresh = parsed.getElementById('CartDrawerContent');
    var current = document.getElementById('CartDrawerContent');

    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
    }

    updateCartCount();
  }

  function updateCartCount() {
    var source = document.querySelector('[data-cart-count-source]');
    if (!source) return;

    var count = parseInt(source.textContent.trim(), 10) || 0;

    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  }

  function showCartError(form, message) {
    if (!form) return;

    var box = form.querySelector('[data-cart-error]');
    if (!box) return;

    box.textContent = message;
    box.hidden = false;
  }

  function clearCartError(form) {
    if (!form) return;
    var box = form.querySelector('[data-cart-error]');
    if (box) box.hidden = true;
  }

  function addToCart(form) {
    var button = form.querySelector('[type="submit"]');
    var label = button ? button.querySelector('[data-button-label]') || button : null;
    var originalText = label ? label.textContent : '';

    clearCartError(form);

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
      if (label) label.textContent = settings.strings ? settings.strings.adding : 'Adicionando...';
    }

    var body = new FormData(form);
    body.append('sections', CART_SECTION);

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        applyCartSections(data.sections);
        openCart();
      })
      .catch(function (error) {
        var message =
          (error && error.description) ||
          (error && error.message) ||
          (settings.strings && settings.strings.addError) ||
          'Não foi possível adicionar o produto à sacola.';
        showCartError(form, message);
      })
      .finally(function () {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
          if (label) label.textContent = originalText;
        }
      });
  }

  function changeQuantity(line, quantity) {
    var drawer = document.getElementById('CartDrawerContent');
    if (drawer) drawer.classList.add('is-busy');

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: [CART_SECTION]
      })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        applyCartSections(data.sections);
      })
      .catch(function (error) {
        console.error('Erro ao atualizar o carrinho:', error);
      })
      .finally(function () {
        if (drawer) drawer.classList.remove('is-busy');
      });
  }

  var saveCartNote = debounce(function (value) {
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value })
    }).catch(function (error) {
      console.error('Erro ao salvar a observação:', error);
    });
  }, 500);

  /* ==================== DELEGAÇÃO GLOBAL ==================== */

  function initDelegatedEvents() {
    document.addEventListener('click', function (event) {
      var openCartBtn = event.target.closest('[data-open-cart]');
      if (openCartBtn) {
        event.preventDefault();
        openCart();
        return;
      }

      var openDrawerBtn = event.target.closest('[data-open-drawer]');
      if (openDrawerBtn) {
        event.preventDefault();
        openDrawer(document.querySelector(openDrawerBtn.dataset.openDrawer));
        return;
      }

      if (event.target.closest('[data-close-cart], [data-close-drawer], .cart-drawer-overlay.is-open, .drawer-overlay.is-open')) {
        closeDrawer();
        return;
      }

      var qtyBtn = event.target.closest('[data-qty-change]');
      if (qtyBtn) {
        event.preventDefault();
        changeQuantity(parseInt(qtyBtn.dataset.line, 10), parseInt(qtyBtn.dataset.quantity, 10));
      }
    });

    document.addEventListener('submit', function (event) {
      var form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      event.preventDefault();
      addToCart(form);
    });

    document.addEventListener('input', function (event) {
      if (event.target.matches('[data-cart-note]')) {
        saveCartNote(event.target.value);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && activeDrawer) {
        closeDrawer();
        return;
      }
      trapFocus(event);
    });
  }

  /* ==================== PDP: GALERIA ==================== */

  function initPdpGallery() {
    var mainImage = document.getElementById('PdpMainImage');
    if (!mainImage) return;

    document.addEventListener('click', function (event) {
      var thumb = event.target.closest('.pdp-thumbnail');
      if (!thumb) return;

      document.querySelectorAll('.pdp-thumbnail').forEach(function (item) {
        item.classList.remove('active');
        item.setAttribute('aria-current', 'false');
      });

      thumb.classList.add('active');
      thumb.setAttribute('aria-current', 'true');

      var full = thumb.dataset.fullSrc;
      if (full) {
        mainImage.src = full;
        mainImage.srcset = '';
      }
    });
  }

  /* ==================== PDP: VARIANTES ==================== */

  function initVariantPicker() {
    var picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;

    var dataEl = document.getElementById('VariantData');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('Não foi possível ler as variantes do produto:', error);
      return;
    }

    var idInput = document.getElementById('SelectedVariantId');
    var priceWrap = document.querySelector('[data-variant-price]');
    var addButton = document.querySelector('[data-add-button]');
    var addLabel = addButton ? addButton.querySelector('[data-button-label]') : null;
    var mainImage = document.getElementById('PdpMainImage');

    // A ordem das opções importa: o Shopify guarda options como um array
    // posicional (options[0] = primeira opção do produto). Casar por "o valor
    // existe em algum lugar" quebra quando duas opções compartilham um valor.
    function selectedOptions() {
      return Array.prototype.map.call(
        picker.querySelectorAll('[data-option-position]'),
        function (group) {
          var selected = group.querySelector('.pdp-variant-pill.is-selected');
          return selected ? selected.dataset.value : null;
        }
      );
    }

    function findVariant(options) {
      return variants.find(function (variant) {
        return options.every(function (value, index) {
          return value === null || variant.options[index] === value;
        });
      });
    }

    function markUnavailable(options) {
      picker.querySelectorAll('[data-option-position]').forEach(function (group) {
        var position = parseInt(group.dataset.optionPosition, 10);

        group.querySelectorAll('.pdp-variant-pill').forEach(function (pill) {
          var candidate = options.slice();
          candidate[position] = pill.dataset.value;

          var match = variants.find(function (variant) {
            return candidate.every(function (value, index) {
              return value === null || variant.options[index] === value;
            });
          });

          var available = !!(match && match.available);
          pill.classList.toggle('is-unavailable', !available);
          pill.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
      });
    }

    function update() {
      var options = selectedOptions();
      var variant = findVariant(options);

      markUnavailable(options);

      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
          if (addLabel) addLabel.textContent = 'Indisponível';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (addButton) {
        addButton.disabled = !variant.available;
        if (addLabel) {
          addLabel.textContent = variant.available
            ? addButton.dataset.labelAvailable
            : addButton.dataset.labelSoldOut;
        }
      }

      if (priceWrap && variant.id) {
        fetch(window.location.pathname + '?variant=' + variant.id + '&section_id=' + priceWrap.dataset.sectionId)
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var fresh = parsed.querySelector('[data-variant-price]');
            if (fresh) priceWrap.innerHTML = fresh.innerHTML;
          })
          .catch(function () {
            /* preço continua o anterior; não vale quebrar a página por isso */
          });
      }

      if (mainImage && variant.featured_image && variant.featured_image.src) {
        mainImage.src = variant.featured_image.src;
        mainImage.srcset = '';
      }

      // Mantém a URL compartilhável apontando para a variante escolhida.
      if (window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    picker.addEventListener('click', function (event) {
      var pill = event.target.closest('.pdp-variant-pill');
      if (!pill) return;

      var group = pill.closest('[data-option-position]');
      if (!group) return;

      group.querySelectorAll('.pdp-variant-pill').forEach(function (item) {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });

      pill.classList.add('is-selected');
      pill.setAttribute('aria-pressed', 'true');

      update();
    });

    update();
  }

  /* ==================== INIT ==================== */

  onReady(function () {
    // window.Shopify.routes existe em toda vitrine, mas garantimos o fallback
    // para o tema não quebrar em pré-visualizações sem o objeto carregado.
    window.Shopify = window.Shopify || {};
    window.Shopify.routes = window.Shopify.routes || { root: '/' };

    initDelegatedEvents();
    initPdpGallery();
    initVariantPicker();
    updateCartCount();
  });
})();
